import { each, uniq } from "lodash-es";
import type { UseCommonData_MasterPrefectureFragment as MasterPrefectureFragment } from "@hooks/useCommonData/__generated__/fragments";
import type { UseCommonData_CommonDataQuery } from "@hooks/useCommonData/__generated__/queries";
import type { SearchCondition } from "@hooks/useSearchCondition";
import { getTerminatingStaticURLParameters } from "@libs/rewrite";
import { toJpDate } from "@libs/utils/date";
import type { Og_MatchFragment } from "@libs/utils/og/__generated__/fragments";
import {
  getDynamicArea,
  getDynamicTeam,
  seoTournamentName,
  trimSpaces,
} from "./common";
import type { Og_ShopsStaticUrlParamsQuery as ShopsStaticUrlParamsQuery } from "./__generated__/queries";
import type { Shops_ShopsIndexPageQuery } from "../../../pages/shops/__generated__/queries";
import type { QueryResult } from "@apollo/client";
import type { ParsedUrlQuery } from "querystring";

const getPrefixFromAreaAndTeam = (
  area: string | undefined,
  team: string | undefined
): string => {
  const prefix = (() => {
    if (area && team) {
      return `${area}で${team}の試合を観戦できる`;
    }

    if (area) {
      return `${area}の`;
    }

    if (team) {
      return `${team}の試合を観戦できる`;
    }
  })();
  return trimSpaces(prefix || "");
};

const getPrefectureNames = (
  masterPrefectures: MasterPrefectureFragment[],
  combinedAreaIds: number[],
  areaIds: number[]
) => {
  const prefectureNames: Array<string> = [];

  each(masterPrefectures, (pref) => {
    each(pref.combinedAreas, (combinedArea) => {
      if (combinedAreaIds.includes(combinedArea.id)) {
        prefectureNames.push(pref.name);
      }
      each(combinedArea.areas, (area) => {
        if (areaIds.includes(area.id)) {
          prefectureNames.push(pref.name);
        }
      });
    });
  });

  return uniq(prefectureNames);
};

const getAreaPrefix = (prefectureNames: string[]) => {
  if (prefectureNames.length === 1) {
    return `${prefectureNames[0]}で`;
  }
  return "";
};

const getMatchText = (match: Og_MatchFragment | undefined) => {
  return match
    ? `${toJpDate(match?.startsAt, "dateOnly")}：${
        match.homeTeamDisplayFullName
      }vs${match.awayTeamDisplayFullName}`
    : "";
};

// these titles and descriptions follow similar patterns but are different from the other
// patterns ergo we need a separate function 'WithMatch'
const getDynamicPrefixWithMatch = (
  searchCondition: SearchCondition,
  masterPrefectures: MasterPrefectureFragment[],
  match: Og_MatchFragment | undefined
) => {
  const { areaIds = [], combinedAreaIds = [] } = searchCondition;
  const prefectureNames = getPrefectureNames(
    masterPrefectures,
    combinedAreaIds,
    areaIds
  );

  return (
    getAreaPrefix(prefectureNames) + getMatchText(match) + "の試合を観戦できる"
  );
};

const getOgFromPrefix = (dynamicPrefix: string): [string, string] => {
  const title = dynamicPrefix + "スポーツバー";

  if (!dynamicPrefix) {
    return [
      title,
      "スポーツ観戦・ライブビューイングが楽しめるスポーツバー探すならFansta(ファンスタ)！試合日程、近くのエリア、好きなチームから目的の試合を上映しているお店を見つけてスポーツ観戦・ライブビューイングを楽しもう。",
    ];
  }
  const description =
    title +
    "を掲載中！試合日程、近くのエリア、好きなチームから見たい試合を上映しているお店を探して予約するならFansta(ファンスタ)。スポーツ観戦・スポーツライブビューイングを楽しもう。";

  return [title, description];
};

const getOgFromSearchCondition = (
  searchCondition: SearchCondition | null,
  data: (Shops_ShopsIndexPageQuery & UseCommonData_CommonDataQuery) | undefined
): [string, string] => {
  if (!searchCondition || !data) {
    return getOgFromPrefix("");
  }

  const dynamicPrefix = searchCondition.matchId
    ? getDynamicPrefixWithMatch(
        searchCondition,
        data.masterPrefectures,
        data.match ?? undefined
      )
    : getPrefixFromAreaAndTeam(
        getDynamicArea(searchCondition, data.masterPrefectures),
        getDynamicTeam(searchCondition, data.teamsUnbundled)
      );

  return getOgFromPrefix(dynamicPrefix);
};

const getAreaTextFromStaticURLParameters = (
  area: string | undefined,
  data: QueryResult<ShopsStaticUrlParamsQuery>["data"]
) => {
  switch (area) {
    case "prefecture":
      return `${
        data?.masterPrefectures ? data?.masterPrefectures[0].name ?? "" : ""
      }`;
    case "combinedArea":
      return `${data?.combinedAreas?.nodes[0].name ?? ""}`;
    case "area":
      return `${data?.areas?.nodes[0].name ?? ""}`;
    default:
      return "";
  }
};

const getTeamTextFromStaticURLParameters = (
  team: string | undefined,
  data: QueryResult<ShopsStaticUrlParamsQuery>["data"],
  match?: Og_MatchFragment
) => {
  switch (team) {
    case "genre":
      return `${data?.sportGenres[0].name ?? ""}`;
    case "tournament":
      return `${seoTournamentName(data?.tournaments)}`;
    case "team":
      return `${data?.teamsUnbundled[0].name ?? ""}`;
    case "matchId":
      return getMatchText(match);
    default:
      return "";
  }
};

const getStaticPrefix = (
  query: ParsedUrlQuery,
  data: QueryResult<ShopsStaticUrlParamsQuery>["data"],
  match?: Og_MatchFragment | undefined
) => {
  const { area, team } = getTerminatingStaticURLParameters(query);
  return getPrefixFromAreaAndTeam(
    getAreaTextFromStaticURLParameters(area, data),
    getTeamTextFromStaticURLParameters(team, data, match)
  );
};

const getOgFromParsedUrlQuery = (
  query: ParsedUrlQuery,
  data: QueryResult<ShopsStaticUrlParamsQuery>["data"],
  match?: Og_MatchFragment | undefined
) => {
  const staticPrefix = getStaticPrefix(query, data, match);

  return getOgFromPrefix(staticPrefix);
};
export {
  getPrefixFromAreaAndTeam,
  getDynamicPrefixWithMatch,
  getOgFromPrefix,
  getOgFromSearchCondition,
  getStaticPrefix,
  getOgFromParsedUrlQuery,
};
